import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import PaymentLogos from "@/components/PaymentLogos";
import { Check, ArrowRight, LogOut, ArrowLeft, Loader2, AlertTriangle, Zap, ShieldCheck } from "lucide-react";
import { toast, Toaster } from "sonner";

const featureKeys = [
  "billing.feature.live_rates",
  "billing.feature.pos",
  "billing.feature.ai",
  "billing.feature.vault",
  "billing.feature.multi_lang",
  "billing.feature.security",
];

// Format Turkish GSM as `5xx xxx xx xx` while typing.
const formatGsm = (raw) => {
  const d = String(raw || "").replace(/\D/g, "").slice(-10); // strip country code, keep last 10
  const p = [d.slice(0, 3), d.slice(3, 6), d.slice(6, 8), d.slice(8, 10)].filter(Boolean);
  return p.join(" ");
};

export default function Pricing() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const { hasActive, isTrial, refresh: refreshSub } = useSubscription();
  const [checkoutError, setCheckoutError] = useState(null);
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [mockMode, setMockMode] = useState(false);
  const [step, setStep] = useState("plan"); // plan | form | iframe
  const [iframeUrl, setIframeUrl] = useState(null);
  const [merchantOid, setMerchantOid] = useState(null);
  const [customer, setCustomer] = useState({
    name: user?.name?.split(" ")[0] || "",
    surname: user?.name?.split(" ").slice(1).join(" ") || "",
    email: user?.email || "",
    gsmNumber: "",
    identityNumber: "",
    address: "",
    city: "",
    zipCode: "",
  });
  const [loading, setLoading] = useState(false);
  const [mockLoading, setMockLoading] = useState(false);

  useEffect(() => {
    api.get("/billing/plans").then(r => {
      setPlan(r.data.plans[0]);
      setMockMode(!!r.data.mock_mode);
    }).catch(()=>{});
  }, []);

  useEffect(() => {
    // Only bounce PAID subscribers away from the pricing page. Trial users are
    // allowed to browse the plans and upgrade at any time.
    if (hasActive && !isTrial) nav("/dashboard", { replace: true });
  }, [hasActive, isTrial, nav]);

  const mockActivate = async () => {
    setMockLoading(true);
    try {
      await api.post("/billing/mock-activate", {});
      toast.success("Test erişimi aktifleştirildi. Panele yönlendiriliyorsunuz…");
      await refreshSub();
      setTimeout(() => nav("/dashboard", { replace: true }), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aktivasyon başarısız");
      setMockLoading(false);
    }
  };

  const startCheckout = async (e) => {
    e.preventDefault();
    if (!/^\d{11}$/.test(customer.identityNumber)) {
      toast.error("TC Kimlik Numarası 11 haneli olmalı"); return;
    }
    const gsmDigits = String(customer.gsmNumber || "").replace(/\D/g, "");
    if (gsmDigits.length !== 10 || !gsmDigits.startsWith("5")) {
      toast.error("Geçerli bir GSM numarası girin (5xx xxx xx xx)"); return;
    }
    setLoading(true);
    setCheckoutError(null);
    try {
      const r = await api.post("/billing/checkout", {
        plan_id: "standart_monthly",
        origin_url: window.location.origin,
        customer: {
          ...customer,
          gsmNumber: "+90" + gsmDigits,
        },
      });
      // PayTR returns an embeddable iframe URL — no full-page redirect.
      const url = r.data.iframe_url;
      if (!url) throw new Error("PayTR ödeme sayfası açılamadı");
      setIframeUrl(url);
      setMerchantOid(r.data.merchant_oid);
      setStep("iframe");
      // Poll for payment status — PayTR callback updates it asynchronously.
      const pollId = setInterval(async () => {
        try {
          const s = await api.get("/billing/subscription");
          if (s.data.has_active && !s.data.is_trial) {
            clearInterval(pollId);
            toast.success("Ödeme onaylandı — panele yönlendiriliyorsunuz…");
            await refreshSub();
            setTimeout(() => nav("/dashboard", { replace: true }), 700);
          }
        } catch {}
      }, 5000);
      // Auto-clear polling after 15 min
      setTimeout(() => clearInterval(pollId), 15 * 60 * 1000);
      setLoading(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Ödeme başlatılamadı";
      const httpCode = err?.response?.status || 0;
      const rawResponse = err?.response?.data || null;
      setCheckoutError({ detail, httpCode, raw: rawResponse });
      toast.error(detail, { duration: 8000 });
      setLoading(false);
    }
  };

  const fmtTL = (n) => Number(n).toLocaleString("tr-TR", { maximumFractionDigits: 0 });

  return (
    <div className="min-h-screen relative overflow-hidden" style={{background:"var(--bg)"}} data-testid="pricing-page">
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1583902340370-6a3fc847d373?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHxnb2xkJTIwY29pbiUyMHN0YWNrfGVufDB8fHx8MTc4NTc1MDE5OXww&ixlib=rb-4.1.0&q=85')",
        backgroundSize: "cover", backgroundPosition: "center",
      }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.85), rgba(5,5,5,0.98))" }} />

      <div className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-5 border-b border-white/8">
        <div>
          <div className="font-display text-2xl sm:text-3xl tracking-widest" style={{color:"var(--gold)"}}>MİLYEMSOFT</div>
          <div className="label-caps mt-1">{t("app.tagline")}</div>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher compact />
          {user && (
            <button onClick={()=>{ logout(); nav("/login"); }} className="p-2 text-neutral-400 hover:text-white" title={t("nav.logout")} data-testid="pricing-logout">
              <LogOut size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-10 py-10 sm:py-16">
        {step === "plan" && (
          <>
            {mockMode && (
              <div className="mb-8 border border-yellow-500/30 bg-yellow-500/5 p-5 flex items-start gap-4 max-w-2xl mx-auto" data-testid="mock-mode-banner">
                <AlertTriangle size={22} className="mt-0.5 shrink-0" style={{color:"#e2b846"}} />
                <div className="text-sm">
                  <div className="font-semibold" style={{color:"#e2b846"}}>PayTR Onayı Bekleniyor · Test Erişimi Aktif</div>
                  <p className="text-neutral-400 mt-1 leading-relaxed">
                    PayTR Abonelik API'sinin merchant hesabımıza tanımlanması sürüyor. Bu süre boyunca <strong className="text-white">"Test Erişimi Al"</strong> butonuna basarak sisteme <strong className="text-white">30 gün ücretsiz</strong> giriş yapabilirsiniz.
                    PayTR onayı geldiğinde bu banner kaldırılır ve gerçek ödeme aktifleşir.
                  </p>
                </div>
              </div>
            )}

            <div className="text-center max-w-2xl mx-auto">
              <div className="label-caps">{t("billing.pricing_title")}</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light mt-4 leading-tight">{t("billing.pricing_headline")}</h1>
              <p className="text-neutral-400 mt-6 text-sm sm:text-base leading-relaxed">{t("billing.pricing_subline")}</p>
              <div className="inline-flex items-center gap-2 mt-6 px-4 py-2 border rounded-full text-xs font-mono tracking-wider"
                   style={{ borderColor: "rgba(212,175,55,0.4)", background: "rgba(212,175,55,0.06)", color: "var(--gold)" }}
                   data-testid="trial-badge">
                <ShieldCheck size={13} /> 30 GÜN ÜCRETSİZ DENEYİN · KART İSTENMEZ
              </div>
            </div>

            <div className="mt-14 max-w-md mx-auto">
              <div className="border border-white/10 bg-[#0a0a0a] relative" style={{boxShadow:"0 40px 80px -20px rgba(212,175,55,0.15)"}} data-testid="plan-card">
                <div className="absolute -top-3 left-6 px-3 py-1 text-[10px] tracking-[0.3em] uppercase font-mono" style={{background:"var(--gold)", color:"#0a0a0a"}}>STANDART</div>
                <div className="p-8 sm:p-10 border-b border-white/8">
                  <div className="label-caps">{plan?.name || "Standart Plan"}</div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="font-display text-5xl sm:text-6xl" style={{color:"var(--gold)"}} data-testid="plan-amount">₺{plan ? fmtTL(plan.amount) : "2.000"}</span>
                    <span className="text-neutral-500 text-sm">{t("billing.per_month")}</span>
                  </div>
                  <p className="text-xs text-neutral-500 mt-3">{plan?.description}</p>
                </div>
                <ul className="p-8 sm:p-10 space-y-3">
                  {featureKeys.map(k => (
                    <li key={k} className="flex items-start gap-3 text-sm">
                      <Check size={16} className="mt-0.5 shrink-0" style={{color:"var(--gold)"}} />
                      <span>{t(k)}</span>
                    </li>
                  ))}
                </ul>
                <div className="p-8 sm:p-10 pt-0 space-y-3">
                  {mockMode ? (
                    <>
                      <button onClick={mockActivate} disabled={mockLoading} data-testid="mock-activate-btn"
                        className="btn-primary-gold w-full flex items-center justify-center gap-2 py-4">
                        {mockLoading ? <><Loader2 size={14} className="animate-spin" /> Aktifleştiriliyor…</> : <><Zap size={14} /> Test Erişimi Al · 30 Gün</>}
                      </button>
                      <p className="text-[11px] text-neutral-600 text-center font-mono">PayTR onayı geldiğinde bu buton yerini gerçek ödemeye bırakır</p>
                    </>
                  ) : (
                    <>
                      <button onClick={()=>setStep("form")} data-testid="subscribe-btn" className="btn-primary-gold w-full flex items-center justify-center gap-2 py-4">
                        {t("billing.subscribe_btn")} <ArrowRight size={14} />
                      </button>
                <p className="text-[11px] text-neutral-600 text-center font-mono">Güvenli ödeme · PayTR Türkiye</p>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="text-center mt-12 label-caps text-neutral-600">{t("footer.trust")}</div>
          </>
        )}

        {step === "form" && (
          <form onSubmit={startCheckout} className="max-w-lg mx-auto" data-testid="paytr-customer-form">
            <button type="button" onClick={()=>setStep("plan")} className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-6" data-testid="back-to-plan">
              <ArrowLeft size={14}/> Plana geri dön
            </button>

            {/* Payment logos — centered and prominent */}
            <div className="mb-6 border p-5 sm:p-6 flex flex-col items-center gap-4"
                 style={{ borderColor: "rgba(212,175,55,0.35)", background: "linear-gradient(135deg, rgba(30,100,255,0.05) 0%, rgba(212,175,55,0.05) 100%)" }}>
              <PaymentLogos size={30} className="justify-center" />
              <div className="text-[11px] font-mono tracking-wider text-neutral-400 flex items-center gap-2 text-center">
                <ShieldCheck size={13} style={{ color: "var(--gold)" }} />
                PayTR · PCI-DSS Level 1 · 3D Secure · TBB Onaylı
              </div>
            </div>

            <div className="mb-2 text-xs tracking-widest uppercase text-neutral-500">Fatura Bilgileri</div>
            <h2 className="font-display text-3xl sm:text-4xl mt-1">Ödeme için birkaç bilgi</h2>
            <p className="text-sm text-neutral-500 mt-3">PayTR'nin güvenli Türkçe ödeme sayfası hemen aşağıda açılacak. Kart bilgileriniz uygulamamıza ulaşmaz.</p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 border border-white/10 bg-[#0a0a0a] p-5 sm:p-6">
              <div>
                <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">Ad</label>
                <input required type="text"
                  placeholder="Örn. Ahmet"
                  value={customer.name}
                  onChange={(e)=>setCustomer({...customer, name: e.target.value})}
                  data-testid="cust-name"
                  className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold] placeholder-neutral-600" />
              </div>
              <div>
                <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">Soyad</label>
                <input required type="text"
                  placeholder="Örn. Yılmaz"
                  value={customer.surname}
                  onChange={(e)=>setCustomer({...customer, surname: e.target.value})}
                  data-testid="cust-surname"
                  className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold] placeholder-neutral-600" />
              </div>
              <div className="sm:col-span-2">
                <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">E-posta</label>
                <input required type="email"
                  placeholder="siz@ornek.com"
                  value={customer.email}
                  onChange={(e)=>setCustomer({...customer, email: e.target.value})}
                  data-testid="cust-email"
                  className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold] placeholder-neutral-600" />
              </div>
              <div>
                <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">GSM</label>
                <div className="flex items-center border border-white/10 focus-within:ring-1 focus-within:ring-[--gold]">
                  <span className="px-3 py-3 text-sm text-neutral-500 border-r border-white/10 font-mono">+90</span>
                  <input required type="tel" inputMode="numeric"
                    placeholder="5xx xxx xx xx"
                    maxLength={13}
                    value={formatGsm(customer.gsmNumber)}
                    onChange={(e)=>setCustomer({...customer, gsmNumber: e.target.value.replace(/\D/g, "").slice(0, 10)})}
                    data-testid="cust-gsm"
                    className="w-full bg-transparent px-3 py-3 text-sm focus:outline-none placeholder-neutral-600 font-mono tracking-wider" />
                </div>
              </div>
              <div>
                <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">TC Kimlik No</label>
                <input required type="text" inputMode="numeric"
                  placeholder="11 haneli"
                  maxLength={11}
                  value={customer.identityNumber}
                  onChange={(e)=>setCustomer({...customer, identityNumber: e.target.value.replace(/\D/g, "").slice(0, 11)})}
                  data-testid="cust-tckn"
                  className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold] placeholder-neutral-600 font-mono tracking-wider" />
              </div>
              <div className="sm:col-span-2">
                <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">Şehir</label>
                <input required type="text"
                  placeholder="İstanbul"
                  value={customer.city}
                  onChange={(e)=>setCustomer({...customer, city: e.target.value})}
                  data-testid="cust-city"
                  className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold] placeholder-neutral-600" />
              </div>
              <div className="sm:col-span-2">
                <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">Adres</label>
                <textarea required rows={2}
                  placeholder="Fatura adresiniz"
                  value={customer.address}
                  onChange={(e)=>setCustomer({...customer, address: e.target.value})}
                  data-testid="cust-address"
                  className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold] placeholder-neutral-600" />
              </div>
            </div>

            <div className="mt-6 p-4 border border-white/8" style={{background:"rgba(212,175,55,0.05)"}}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-sm">Standart Plan · aylık</span>
                <span className="font-display text-2xl" style={{color:"var(--gold)"}}>₺2.000</span>
              </div>
              <div className="text-[11px] text-neutral-500 mt-1">Bir sonraki tahsilat 30 gün sonra otomatik yapılır.</div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary-gold w-full mt-6 py-4 flex items-center justify-center gap-2 text-sm sm:text-base" data-testid="paytr-pay-btn">
              {loading ? <><Loader2 size={14} className="animate-spin"/>Ödeme sayfası hazırlanıyor</> : <>PayTR Güvenli Ödemeye Geç <ArrowRight size={14}/></>}
            </button>
            <p className="text-[11px] text-neutral-600 text-center mt-3 px-4">3D Secure destekli · PayTR PCI-DSS · Türkiye içi banka POS</p>

            {checkoutError && (
              <div className="mt-5 border border-red-500/40 bg-red-500/5 p-4" data-testid="checkout-error-box">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-red-300">Ödeme başlatılamadı</div>
                    <div className="text-xs text-neutral-300 mt-1 break-words">{checkoutError.detail}</div>
                    <details className="mt-3">
                      <summary className="text-[10px] font-mono tracking-widest text-neutral-500 uppercase cursor-pointer hover:text-white">Teknik Detay</summary>
                      <pre className="mt-2 p-3 bg-black/50 border border-white/8 text-[10px] font-mono text-neutral-400 whitespace-pre-wrap break-all overflow-auto max-h-48" data-testid="checkout-error-raw">
{JSON.stringify({ http: checkoutError.httpCode, response: checkoutError.raw }, null, 2)}
                      </pre>
                    </details>
                  </div>
                </div>
              </div>
            )}

            {/* Legal links + logos below form */}
            <div className="mt-6 pt-6 border-t border-white/6 flex flex-col items-center gap-3">
              <PaymentLogos size={24} className="justify-center" />
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[10px] font-mono tracking-widest text-neutral-500">
                <Link to="/legal/mesafeli-satis" className="hover:text-white">MESAFELİ SATIŞ</Link>
                <span className="text-neutral-700">·</span>
                <Link to="/legal/iptal-iade" className="hover:text-white">İPTAL / İADE</Link>
                <span className="text-neutral-700">·</span>
                <Link to="/legal/gizlilik-kvkk" className="hover:text-white">KVKK</Link>
                <span className="text-neutral-700">·</span>
                <Link to="/legal/hizmet-teslimati" className="hover:text-white">HİZMET TESLİMATI</Link>
              </div>
            </div>
          </form>
        )}

        {step === "iframe" && iframeUrl && (
          <div className="max-w-3xl mx-auto" data-testid="paytr-iframe-step">
            <button type="button" onClick={()=>{ setIframeUrl(null); setMerchantOid(null); setStep("form"); setLoading(false); }} className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-6" data-testid="back-to-form">
              <ArrowLeft size={14}/> Bilgi formuna dön
            </button>
            <div className="mb-4">
              <div className="label-caps">PayTR Güvenli Ödeme</div>
              <h2 className="font-display text-2xl sm:text-3xl mt-2">Kart bilgilerinizi PayTR ekranına girin</h2>
              <p className="text-xs text-neutral-500 mt-2 leading-relaxed">
                Ödeme onaylandığı anda otomatik olarak panele yönlendirileceksiniz.
                {merchantOid && <span className="font-mono text-neutral-600 ml-1">Sipariş no: {merchantOid}</span>}
              </p>
            </div>
            <iframe
              title="PayTR güvenli ödeme"
              src={iframeUrl}
              className="w-full border border-white/10 bg-white"
              style={{ minHeight: 720 }}
              data-testid="paytr-iframe"
            />
            <p className="text-[11px] text-neutral-600 text-center mt-4 font-mono">
              PCI-DSS güvenli iframe · Kart bilgileri PayTR sunucularında saklanır · Bu sayfayı kapatmayın
            </p>
          </div>
        )}
      </div>
      <Toaster theme="dark" position="top-right" />
    </div>
  );
}
